import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton } from "@mui/material";
import type { MouseEvent } from "react";

interface RedemptionLinkCellProps {
  url: string;
  /**
   * Asks the panel to copy `url` and report what happened. The clipboard write itself lives
   * there, not here, because it has two failure modes a table cell cannot tell the user about:
   * the write can be denied, and `navigator.clipboard` can be missing outright. The only surface
   * that can say so is the panel's Snackbar, and a cell renderer may not call hooks to reach it
   * (Blotter's hooks-vs-renderers rule).
   */
  onCopy: (url: string) => void;
}

const wrapperSx = {
  display: "flex",
  alignItems: "center",
  gap: 0.5,
  width: "100%",
  minWidth: 0,
  // Two separate keys, not one "&:hover .x, &:focus-within .x" comma list: this app renders
  // MUI's sx styles inside `@layer mui` (see index.tsx's <StyledEngineProvider enableCssLayer>).
  // Under that layer-wrapping, emotion/stylis silently drops a comma-separated compound selector
  // that also uses a descendant combinator -- the rule never reaches any stylesheet, no error, no
  // warning, opacity just never leaves 0. Verified live: two single-selector rules each emit
  // correctly under @layer mui; the combined comma form emits neither. Keep these unmerged.
  "&:hover .redemption-copy-btn": {
    opacity: 1,
  },
  "&:focus-within .redemption-copy-btn": {
    opacity: 1,
  },
} as const;

const linkSx = {
  minWidth: 0,
  flex: "1 1 auto",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  color: "var(--color-rodeo-black)",
  textDecoration: "none",
  "&:hover": {
    textDecoration: "underline",
  },
} as const;

const copyButtonSx = {
  opacity: 0,
  transition: "opacity 0.12s",
  flexShrink: 0,
} as const;

/**
 * Redemption Link cell (PRD section 4 item 8 / R17). Shows the URL without its protocol,
 * truncated with an ellipsis when it doesn't fit (the full URL always sits in `title`), backed
 * by a real `<a target="_blank">` link -- clicking the text navigates like any link.
 *
 * The copy button only becomes visible on hover/focus of the cell: a CSS opacity flip on the
 * wrapper (`&:hover`/`&:focus-within`), not React state, mirroring the hover-reveal-action
 * pattern already used in GroupPaymentMethodsPanel.tsx. IconButton stays focusable regardless of
 * its opacity, and ":focus-within" reveals it as soon as the link or the button itself receives
 * focus, so the control is reachable by keyboard, not just by mouse hover.
 *
 * Copying always writes the FULL url, protocol included (R17), even though the displayed text
 * hides it, and never triggers the underlying link's navigation (preventDefault +
 * stopPropagation before anything else). See `onCopy` above for why the write itself happens in
 * the panel, not here.
 */
export default function RedemptionLinkCell({ url, onCopy }: RedemptionLinkCellProps) {
  const displayText = url.replace(/^https?:\/\//, "");

  function handleCopyClick(event: MouseEvent<HTMLButtonElement>) {
    // The button sits inside the <a>: without both of these, copying also navigates the row.
    event.preventDefault();
    event.stopPropagation();
    onCopy(url);
  }

  return (
    <Box sx={wrapperSx}>
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        title={url}
        sx={linkSx}
      >
        {displayText}
      </Box>
      <IconButton
        className="redemption-copy-btn"
        aria-label="Copy redemption link"
        size="small"
        onClick={handleCopyClick}
        sx={copyButtonSx}
      >
        <ContentCopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </Box>
  );
}
